import { observable, action, computed } from "mobx";

import { ListWrapper, EmptyWrapper } from "src/model/ListWrapper";

import { BaseStore } from "./BaseStore";

export const FIRST_PAGE: number = 1;
export const DEFAULT_SIZE_PER_PAGE: number = 10;

export type GenericUpdateObject = { [key: string]: string | number | null };

export interface BaseListState {
    page: number;
    sizePerPage: number;
    sortOrder?: "asc" | "desc";
    sortField?: string;
}

export abstract class BaseListStore<ListFilters extends {}, ListState extends BaseListState, O> extends BaseStore {
    @observable protected _listFilters: ListFilters = this.defaultFilters();
    @observable protected _listState: ListState = this.defaultState();

    @observable protected _list: ListWrapper<O> = EmptyWrapper();
    private mostRecentRequest: number = 1;

    constructor() {
        super();
    }

    /**
     * This should be called between page navigations to ensure a clean store.
     * (Call from `componentDidMount()` in most cases)
     */
    @action resetAndRefresh(clearFirst: boolean = true): Promise<ListWrapper<O>> {
        this._listFilters = this.defaultFilters();
        this._listState = this.defaultState();
        return this.refresh(clearFirst);
    }

    @action reset(clearContents: boolean = true): void {
        this._listFilters = this.defaultFilters();
        this._listState = this.defaultState();
        if (clearContents) {
            this._list = EmptyWrapper();
        }
    }

    @action async refresh(clearFirst: boolean = true): Promise<ListWrapper<O>> {
        this.setLoading();
        try {
            // Start by resetting the old values
            if (clearFirst) {
                this._list = EmptyWrapper();
            }
            // Go to server to obtain values
            const requestNo = ++this.mostRecentRequest;
            const list = await this.obtainList();

            // Only update if this is the most recent request
            if (requestNo === this.mostRecentRequest) {
                this._list = list;
                this.setReady();
            }
            return this._list;
        } catch (e) {
            this.setError(e ? e.message || e : "An unknown error occurred");
            throw e;
        }
    }

    @action updateListFilters(filters: Partial<ListFilters>, refresh: boolean = true) {
        Object.keys(filters).forEach(key => (this._listFilters[key] = filters[key]));
        // Reset to the first page whenever a filter is changed
        this._listState.page = FIRST_PAGE;
        if (refresh) {
            this.refresh();
        }
    }

    @action updateListState(state: Partial<ListState>, refresh: boolean = true) {
        Object.keys(state).forEach(key => (this._listState[key] = state[key]));
        if (refresh) {
            this.refresh();
        }
    }

    @computed get listFilters(): ListFilters {
        return this._listFilters;
    }

    @computed get listState(): ListState {
        return this._listState;
    }

    @computed get list(): ListWrapper<O> {
        return this._list;
    }

    protected abstract obtainList(): Promise<ListWrapper<O>>;
    protected abstract defaultFilters(): ListFilters;
    protected defaultState(): ListState {
        return {
            page: FIRST_PAGE,
            sizePerPage: DEFAULT_SIZE_PER_PAGE
        } as ListState;
    }
}



// WEBPACK FOOTER //
// ./src/stores/BaseListStore.ts